import Styled, { css } from 'styled-components';

import { FONT_MAP } from '../../../lib/StyledComponents/StyledComponents';
import { SVGIconStyleWrapper } from '../../Icon/SVGIcon';
import { FormHelperTxt, FormLabel } from '../Form/FormStyles';
import { SelectInputContainer } from '../Form/SelectInput/SelectInputStyles';
import { InputContainer, StyledInput } from '../Form/TextInput/InputStyles';
import { cssDeclaration, VALID_DIMENSION_DATA } from '../Utils';

export const TRANSITION_DURATION: number = 150;

export interface ITooltipStyles {
  marginControl?: 'top' | 'bottom';
}

export const TooltipContainer = Styled.div`
  position: relative;
  display: inline-block;
  cursor: pointer;

  ${({ theme }) => {
    const { themeTwo } = theme;

    return css`
      z-index: ${themeTwo.global.zIndex.middle};
      transform: translateY(${themeTwo.tooltip.pane.verticalOffset ? themeTwo.tooltip.pane.verticalOffset : '15%'});

      ${themeTwo.tooltip.activationType === 'automatic' &&
      css`
        &:hover > ${SVGIconStyleWrapper} {
          ${cssDeclaration(themeTwo, 'color', themeTwo.tooltip.icon.hover)}
        }
      `}

      &:focus:not(:focus-visible) {
        outline: 0;
        box-shadow: none;
      }

      &:focus-visible,
      &:focus-within {
        ${cssDeclaration(themeTwo, 'outline', themeTwo.global.outline)}
      }
    `;
  }}
`;

export const WithTooltipContainer = Styled.div<ITooltipStyles>`
  position: relative;

  & > :first-child {
    display: inline;
  }

  & > ${FormLabel}:first-child {
    display: inline-block;
  }

  ${({ theme: { themeTwo }, marginControl }) => css`
      ${FormLabel} ~ ${TooltipContainer},
      ${FormHelperTxt} ~ ${TooltipContainer},
      ${FormHelperTxt} + div > ${TooltipContainer},
      ${FormLabel} + div > ${TooltipContainer} {
        position: absolute;
        top: 0;
        transform: translateY(${themeTwo.tooltip.pane.verticalOffset ? themeTwo.tooltip.pane.verticalOffset : '10%' });
      }

      ${FormHelperTxt} ~ ${TooltipContainer},
      ${FormHelperTxt} + div > ${TooltipContainer} {
        top: auto;
        bottom: 0;
        transform: translateY(${themeTwo.tooltip.pane.helperTextVerticalOffset ? themeTwo.tooltip.pane.helperTextVerticalOffset : '0px' });
      }

      // moves margin from label and/or helper text to parent to allow correct centering of tooltip
      ${marginControl && css`
        margin-${marginControl}:
          ${themeTwo.global.edgeSize[themeTwo.form.label.margin.bottom] || themeTwo.form.label.margin.bottom};
      `}

      // zeros margin from labels and helper text with tooltips
      ${FormLabel}, ${FormHelperTxt} {
        margin-${marginControl}: 0;
      }

      ${TooltipContainer} {
        /* margin */ ${cssDeclaration(themeTwo, 'margin', themeTwo.tooltip.icon.margin)};
      }
    `
  }

  ${({ theme: { themeTwo }, marginControl }) => {
    if (marginControl === 'top' && themeTwo) {
      const margin =
      themeTwo.global.edgeSize[themeTwo.form.helperText.margin.top] || themeTwo.form.helperText.margin.top;

      return (
        css`
          margin-top: calc((${margin} / 2) + 0.2rem);
        `
      );
    }
  }}

  // these styles offset tooltip icon so it doesn't create an unwanted offset
  ${({ theme: { themeTwo }, marginControl }) => {
    if (marginControl === 'bottom') {
      return css`
        height: 1.375em;

        & ~ ${InputContainer} > ${StyledInput},
        & ~ ${SelectInputContainer} {
          margin-top: ${themeTwo.form.input.withTooltipVerticalOffset}
        }
      `;
    }
  }}
`;

export const TooltipPane = Styled.div<{ widthProp?: VALID_DIMENSION_DATA }>`
  position: absolute;
  margin-top: 0.3rem;
  display: block;
  opacity: 0;
  transition: opacity ${TRANSITION_DURATION}ms ease-out;
  cursor: auto;

  &.active {
    opacity: 1;
    visibility: visible;
  }

  ${({ theme: { themeTwo }, widthProp }) => css`
    z-index: ${themeTwo.global.zIndex.high};
    font-family: ${themeTwo.tooltip.pane.font.family && FONT_MAP[themeTwo.tooltip.pane.font.family]};

    /* margin */ ${cssDeclaration(themeTwo, 'margin', themeTwo.tooltip.pane.margin)};
    /* background-color */${cssDeclaration(themeTwo, 'background-color', themeTwo.tooltip.pane.background)};
    /* color */${cssDeclaration(themeTwo, 'color', themeTwo.tooltip.pane.font.color)};
    /* box-shadow */${cssDeclaration(themeTwo, 'box-shadow', themeTwo.tooltip.pane.shadow)};
    /* font-size */ ${cssDeclaration(themeTwo, 'font-size', themeTwo.tooltip.pane.font.size)};
    /* line-height */ ${cssDeclaration(themeTwo, 'line-height', themeTwo.tooltip.pane.font.lineHeight)};
    /* padding */ ${cssDeclaration(themeTwo, 'padding', themeTwo.tooltip.pane.padding)};
    /* border-radius */ ${cssDeclaration(themeTwo, 'border-radius', themeTwo.tooltip.pane.round)};
    /* border */ ${themeTwo.tooltip.pane.border && cssDeclaration(themeTwo, 'border', themeTwo.tooltip.pane.border)};

    ${widthProp && css`
      max-width: none;
      /* width */ ${cssDeclaration(themeTwo, 'width', widthProp)};
    `}

    ${themeTwo.tooltip.pane.position}: 100%;
  `}
`;
